import React from 'react';
import ReactDOM from 'react-dom/client';
import './utils/threeWarningFilter'; // silence R3F-driven THREE.Clock r183 deprecation
import App from './app/App';
import { router } from './app/router';
import './i18n'; // i18n must init before App renders
import './index.css';
import { initSentry } from './utils/sentry';
import { initAnalytics } from './utils/analytics';
import {
  ONBOARDING_CACHE_RESET_PARAM,
  ONBOARDING_CACHE_RESET_VERSION,
  hasCurrentOnboardingCacheResetMarker,
  writeCurrentOnboardingCacheResetMarker,
} from './app/onboardingCacheReset';

// Initialize Sentry error tracking (before React renders)
initSentry();

// Initialize user analytics
initAnalytics();

function hasOnboardingCacheResetMarker(): boolean {
  try {
    return hasCurrentOnboardingCacheResetMarker(
      window.localStorage,
      new URL(window.location.href),
    );
  } catch {
    return false;
  }
}

function writeOnboardingCacheResetMarker(): void {
  writeCurrentOnboardingCacheResetMarker(window.localStorage);
}

async function resetControlledOnboardingCacheIfNeeded(): Promise<boolean> {
  if (!import.meta.env.PROD) return false;
  if (window.location.pathname !== '/onboarding') return false;
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return false;
  if (hasOnboardingCacheResetMarker()) return false;

  try {
    const [registrations, cacheNames] = await Promise.all([
      navigator.serviceWorker.getRegistrations(),
      'caches' in window ? caches.keys() : Promise.resolve([]),
    ]);

    await Promise.all([
      ...registrations.map((registration) => registration.unregister()),
      ...cacheNames.map((name) => caches.delete(name)),
    ]);

    writeOnboardingCacheResetMarker();
    const next = new URL(window.location.href);
    next.searchParams.set(ONBOARDING_CACHE_RESET_PARAM, ONBOARDING_CACHE_RESET_VERSION);
    next.searchParams.set('t', String(Date.now()));
    window.location.replace(next.toString());
    return true;
  } catch {
    // If browser cache APIs fail, continue rendering and let in-app recovery handle it.
    return false;
  }
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;

  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloadScheduled = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloadScheduled) return;
    reloadScheduled = true;

    try {
      const now = Date.now();
      const lastReload = Number(sessionStorage.getItem('sentry-sw-controller-reload') ?? '0');
      if (now - lastReload < 30_000) return;
      sessionStorage.setItem('sentry-sw-controller-reload', String(now));
    } catch {
      // sessionStorage can be unavailable; one reload is still the right recovery.
    }

    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        registration.update().catch(() => {
          // Update checks are best effort; normal navigation still works.
        });

        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });

        registration.addEventListener('updatefound', () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;
          installingWorker.addEventListener('statechange', () => {
            if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
              installingWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {
        // SW registration failed — app works fine without it
      });
  });
}

function renderApp() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App router={router} />
    </React.StrictMode>
  );
}

resetControlledOnboardingCacheIfNeeded()
  .then((redirecting) => {
    if (!redirecting) {
      registerServiceWorker();
      renderApp();
    }
  })
  .catch(() => {
    registerServiceWorker();
    renderApp();
  });
